import { BlockStack, Card, InlineStack, Layout, Page, Text } from '@shopify/polaris';
import { ReactNode } from 'react';
import './LegacyDocumentationPage.css';

const imageRoot = '/legacy-documentation';

const jquerySnippet = `<script src="https://ajax.googleapis.com/ajax/libs/jquery/3.5.1/jquery.min.js"></script>`;

const searchBarSnippet = `<!--GoVedia -->
<div class="page-width wrapper container">
  <div class="gv-mmy-bar">
    <div id="gv-mmy"></div>
    <script type="text/javascript">
      var MMY_Collection = {% if template contains 'collection' %}true{% else %}false{% endif %};
      var MMY_Tags = '{{ current_tags }}';
      var MMY_Handle = '{{ collection.handle }}';
      var MMY_Customer = '{{ customer.id }}';
      var MMY_Vehicles = '{{ customer.metafields.global.mmy }}';
      var MMY_CustomerTags = '{{ customer.tags | join: "," }}';
      var MMY_CollectionMetaField = '{{ collection.metafields.custom.make }}';
    </script>
  </div>
</div>
<!--End GoVedia-->`;

const loadingSearchBarSnippet = `<!--GoVedia -->
<div class="page-width wrapper container">
  <div class="gv-mmy-bar">
    <div id="gv-mmy">
      <img src="{{ 'spinner.gif' | asset_url }}" alt="Loading..." />
    </div>
    <script type="text/javascript">
      var MMY_Collection = {% if template contains 'collection' %}true{% else %}false{% endif %};
      var MMY_Tags = '{{ current_tags }}';
      var MMY_Handle = '{{ collection.handle }}';
      var MMY_Customer = '{{ customer.id }}';
      var MMY_Vehicles = '{{ customer.metafields.global.mmy }}';
      var MMY_CollectionMetaField = '{{ collection.metafields.custom.make }}';
    </script>
  </div>
</div>
<!--End GoVedia-->`;

const horizontalCss = `.gv-mmy-bar {float:left; width:100%; margin:10px 0 10px 0;}
.gv-mmy-bar .mmy-tab {float:left; margin-right:20px;}
.gv-mmy-bar .mmy-tab select {padding:5px 10px 5px 10px !important; height:35px; min-width:200px;}
.gv-mmy-bar .mmy-tab .gv-btn-search {height:35px; padding:5px 10px 5px 10px; margin:0 10px 0 0;}
.gv-mmy-bar .mmy-tab .gv-btn-clear {height:35px; padding:5px 10px 5px 10px;}
.mmy-tab.mmy-make.mmy-metadataenabled {width:100%;}
.mmy-metadatafullwidth {width:100%; margin:0 !important; line-height:40px;}
.mmy-metadatafullwidth select {width:100%;}
.gv-arrow-down:after {content:url('https://car.govedia.com/Images/down-arrow.png'); display:block; width:100px; margin:0 auto !important;}
.gv-hide {display:none;}
.gv-arrow-down-model::before {content:"Choose Sub Model"; text-align:center; margin:0 auto; display:block; font-weight:bold;}
.gv-arrow-down-year::before {content:"Filter by Year"; text-align:center; margin:0 auto; display:block; font-weight:bold;}`;

const verticalCss = `.gv-mmy-bar {float:left; width:100%; margin:10px 0 10px 0;}
.gv-mmy-bar .mmy-tab {float:left; margin-right:20px; clear:left;}
.gv-mmy-bar .mmy-tab select {padding:5px 10px 5px 10px !important; height:35px; min-width:200px;}
.gv-mmy-bar .mmy-tab .gv-btn-search {height:35px; padding:5px 10px 5px 10px; margin:0 10px 0 0; min-width:128px;}
.gv-mmy-bar .mmy-tab .gv-btn-clear {height:35px; padding:5px 10px 5px 10px;}
.mmy-tab.mmy-make.mmy-metadataenabled {width:100%;}
.mmy-metadatafullwidth {width:100%; margin:0 !important; line-height:40px;}
.mmy-metadatafullwidth select {width:100%;}
.gv-arrow-down:after {content:url('https://car.govedia.com/Images/down-arrow.png'); display:block; width:100px; margin:0 auto !important;}
.gv-hide {display:none;}
.gv-arrow-down-model::before {content:"Choose Sub Model"; text-align:center; margin:0 auto; display:block; font-weight:bold;}
.gv-arrow-down-year::before {content:"Filter by Year"; text-align:center; margin:0 auto; display:block; font-weight:bold;}`;

const productFitmentSnippet = `<!--GoVedia -->
<div id="gv-mmy-desc" style="display:none">
  Vehicle Fitment: <a id="gv-back-to-results" class="title"></a>
</div>
<!--End GoVedia -->`;

const productTagsSnippet = `<!--GoVedia-->
<div id="gv-prod-tags" style="display:none;">
  {% for tag in product.tags %}<tag>{{ tag }}</tag>{% endfor %}
</div>
<!--End GoVedia-->`;

const garageSnippet = `<!--GoVedia -->
<div id="gv-save-vehicles"></div>
<!--End GoVedia -->`;

const garageCss = `.gv-vehicles {
  text-align:center;
  min-width:200px;
  padding:10px 30px;
  position:absolute;
  top:2px;
  right:20px;
  background-color:#ffffff;
  border:1px solid #000000;
  border-radius:0 0 5px 5px;
}
.gv-vehicles .popup {border-top:2px solid #888888; margin:10px; padding:10px;}
.gv-vehicles a {font-size:15px; color:#888888;}
.gv-vehicles ul {text-align:left; margin:10px 0; list-style:none;}
.gv-vehicles ul li {padding:1px 5px; margin:2px 4px; background-color:#bc1d23;}
.gv-vehicles ul li a {color:#ffffff; font-size:13px; display:block;}`;

const ExternalLink = ({ href, children }: { href: string; children: ReactNode }) => (
    <a href={href} target="_blank" rel="noreferrer">{children}</a>
);

const CodeSample = ({ children }: { children: string }) => (
    <pre className="LegacyDocumentation__code"><code>{children}</code></pre>
);

const DocumentationImage = ({ file, alt, narrow = false }: { file: string; alt: string; narrow?: boolean }) => (
    <img
        className={narrow ? 'LegacyDocumentation__image LegacyDocumentation__image--narrow' : 'LegacyDocumentation__image'}
        src={`${imageRoot}/${file}`}
        alt={alt}
        loading="lazy"
    />
);

const DocumentationSection = ({ id, title, children }: { id: string; title: string; children: ReactNode }) => (
    <div id={id} className="LegacyDocumentation__anchor">
        <Card>
            <BlockStack gap="400">
                <Text as="h2" variant="headingLg">{title}</Text>
                {children}
            </BlockStack>
        </Card>
    </div>
);

export const LegacyDocumentationPage = () => (
    <Page title="Documentation">
        <Layout>
            <Layout.Section>
                <BlockStack gap="500">
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingLg">Getting Started</Text>
                            <Text as="p">
                                Before users can begin searching your store by Make, Model, and Year, there are a few steps to get set up.
                            </Text>
                            <div className="LegacyDocumentation__warning">
                                It&apos;s always a good idea to create a backup of any files before making changes. Consider creating a duplicate theme to test your changes before updating your live store.{' '}
                                <ExternalLink href="https://help.shopify.com/manual/online-store/themes/managing-themes/duplicating-themes">Learn how to duplicate a theme</ExternalLink>.
                            </div>
                            <Text as="p">
                                Contact us with any questions or concerns by email at <a href="mailto:support@govedia.com">support@govedia.com</a>, or by phone at 408-758-8845.
                            </Text>

                            <div className="LegacyDocumentation__directory">
                                <div>
                                    <Text as="h3" variant="headingMd">Videos</Text>
                                    <ul>
                                        <li><ExternalLink href="https://car.govedia.com/images/site/aps-setup-testing.mp4">Basic Setup</ExternalLink></li>
                                        <li><a href="#video-tour">Promo Video - Quick Tour</a></li>
                                    </ul>
                                </div>
                                <div>
                                    <Text as="h3" variant="headingMd">Theme Setup</Text>
                                    <p>Select a section for setup instructions.</p>
                                    <ul>
                                        <li><a href="#results-page">Results Page</a> <span className="LegacyDocumentation__required">required</span></li>
                                        <li><a href="#vehicle-search-bar-2">Vehicle Search Bar (2.0 Themes)</a> <span className="LegacyDocumentation__required">required</span></li>
                                        <li><a href="#vehicle-search-bar">Vehicle Search Bar (Vintage Themes)</a> <span className="LegacyDocumentation__required">required</span></li>
                                        <li><a href="#filtering-collections">Filtering Collections</a> <span>optional</span></li>
                                        <li><a href="#filtering-collections-advanced">Filtering Collections (Advanced)</a> <span>optional</span></li>
                                        <li><a href="#product-page-updates">Product Page Updates</a> <span>optional</span></li>
                                        <li><a href="#my-garage">My Garage</a> <span>optional</span></li>
                                    </ul>
                                </div>
                                <div>
                                    <Text as="h3" variant="headingMd">Usage Instructions</Text>
                                    <p>Information about the app&apos;s admin console.</p>
                                    <ul>
                                        <li><a href="#managing-vehicles">Creating &amp; Managing Vehicles</a></li>
                                        <li><a href="#vehicle-assignment">Assigning Vehicles to Products</a></li>
                                    </ul>
                                </div>
                            </div>
                        </BlockStack>
                    </Card>

                    <DocumentationSection id="results-page" title="Results Page">
                        <Text as="p">
                            Follow these steps if an &quot;All&quot; collection does not already exist in your store. Most stores include one by default. Check by opening <strong>your-store.com/collections/all</strong>, replacing the example domain with your store domain.
                        </Text>
                        <ol>
                            <li>Navigate to Collections in the Shopify admin.</li>
                            <li>Create a collection with its title and handle set to <strong>All</strong>.</li>
                            <li>Map every product to this collection. You can do this quickly with an automatic condition matching the example below.</li>
                        </ol>
                        <DocumentationImage file="collection-all.JPG" alt="Example Shopify All collection configuration" />
                        <Text as="p">Save the new collection, and you are all set.</Text>
                    </DocumentationSection>

                    <DocumentationSection id="vehicle-search-bar-2" title="Vehicle Search Bar (2.0 Themes)">
                        <ol>
                            <li>Open your store&apos;s Shopify admin.</li>
                            <li>Go to Online Store to view your current themes.</li>
                            <li>Locate the theme you&apos;d like to add the search bar to, then click <strong>Customize</strong>.</li>
                            <li>Click <strong>Add section</strong> &gt; <strong>Auto Part Search</strong>.</li>
                            <li>Click <strong>Save</strong> to retain your theme updates.</li>
                        </ol>
                        <DocumentationImage file="theme-section.PNG" alt="Adding the Auto Part Search theme section" />
                        <Text as="p">Open the Auto Part Search section to view and edit its styling options. Save theme edits to retain the changes.</Text>
                        <DocumentationImage file="search-bar-colors.PNG" alt="Auto Part Search theme color settings" narrow />
                        <Text as="p">If your theme does not support sections, it may be a Vintage theme. In that case, follow the manual theme-code instructions below.</Text>
                        <div className="LegacyDocumentation__warning">Having trouble integrating the search bar or styling it to match your theme? <a href="mailto:support@govedia.com">Contact us for assistance</a>.</div>
                    </DocumentationSection>

                    <DocumentationSection id="vehicle-search-bar" title="Vehicle Search Bar (Vintage Themes)">
                        <Text as="p">
                            Adding Make, Model, and Year search to your Shopify store requires theme adjustments. The exact changes depend on the theme and desired result. Adding the selector to the master <ExternalLink href="https://shopify.com/admin/themes/current/?key=layout/theme.liquid">theme.liquid</ExternalLink> template displays it throughout the site; the steps below use that approach.
                        </Text>
                        <div className="LegacyDocumentation__tip">
                            <strong>jQuery requirement:</strong> Many legacy themes already include jQuery. If yours does not, add this before the closing head tag in theme.liquid.
                            <CodeSample>{jquerySnippet}</CodeSample>
                        </div>
                        <ol>
                            <li>
                                From your store&apos;s admin, open the theme code editor for the theme you will edit.
                                <DocumentationImage file="edit-html.jpg" alt="Opening the Shopify theme code editor" />
                            </li>
                            <li>
                                In the Layout folder, select the Liquid layout where you want the selector. These instructions use <ExternalLink href="https://shopify.com/admin/themes/current/?key=layout/theme.liquid">theme.liquid</ExternalLink>.
                                <DocumentationImage file="liquid-theme.jpg" alt="Selecting theme.liquid in the Shopify theme editor" />
                            </li>
                            <li>
                                Copy the snippet below into theme.liquid. Search for <strong>content_for_layout</strong> to find the main content area and place the snippet above any wrapping element tags. Adjust its placement to control where the search bar appears.
                                <CodeSample>{searchBarSnippet}</CodeSample>
                            </li>
                        </ol>
                        <div className="LegacyDocumentation__tip">
                            <strong>Advanced tip:</strong> To show a loading image while the app initializes or changes a vehicle selection, include an image inside the #gv-mmy element.
                            <CodeSample>{loadingSearchBarSnippet}</CodeSample>
                        </div>
                        <ol start={4}>
                            <li>
                                Add styling for the selector. In theme.liquid, find the closing head tag shown below.
                                <DocumentationImage file="css-ref.jpg" alt="Closing head tag in theme.liquid" />
                                Add this line above the closing head tag:
                                <CodeSample>{`{{ 'govedia.css' | asset_url | stylesheet_tag }}`}</CodeSample>
                                Save the file.
                                <DocumentationImage file="save-theme-liquid.jpg" alt="Saving theme.liquid" />
                                Expand the Assets folder, select <strong>Add a new asset</strong>, create a blank CSS file named <strong>govedia.css</strong>, and save it.
                                <DocumentationImage file="add-css-file.jpg" alt="Creating govedia.css as a theme asset" />
                            </li>
                        </ol>
                        <div className="LegacyDocumentation__tip"><strong>Tip:</strong> These styles are basic samples. You can implement additional CSS rules to match your theme.</div>
                        <Text as="h3" variant="headingMd">Horizontal Style Block</Text>
                        <DocumentationImage file="horizontal-style.JPG" alt="Horizontal legacy vehicle selector" narrow />
                        <CodeSample>{horizontalCss}</CodeSample>
                        <Text as="h3" variant="headingMd">Vertical Style Block</Text>
                        <DocumentationImage file="vertical-style.JPG" alt="Vertical legacy vehicle selector" narrow />
                        <CodeSample>{verticalCss}</CodeSample>
                        <Text as="p">Save, test, and revise your edits if needed.</Text>
                        <div className="LegacyDocumentation__warning">Having trouble integrating the search bar or styling it to match your theme? <a href="mailto:support@govedia.com">Contact us for assistance</a>.</div>
                    </DocumentationSection>

                    <DocumentationSection id="filtering-collections" title="Filtering Collections">
                        <Text as="p">
                            These steps are optional. Persistent filtering makes collection links automatically retain the shopper&apos;s selected vehicle.
                        </Text>
                        <ol>
                            <li>Open the app&apos;s Settings page.</li>
                            <li>Enable <strong>Enable persistent filtering</strong>.</li>
                            <li>Click <strong>Save Settings</strong> and test the change.</li>
                        </ol>
                        <Text as="p">Some themes may not be compatible with this option. If you experience an issue, <a href="mailto:support@govedia.com">contact us for assistance</a>.</Text>
                    </DocumentationSection>

                    <DocumentationSection id="filtering-collections-advanced" title="Filtering Collections (Advanced)">
                        <Text as="p">
                            If standard persistent filtering does not meet your needs, you can restrict it to specific sections of your store. These advanced steps require theme edits; create a theme backup before proceeding.
                        </Text>
                        <div className="LegacyDocumentation__warning">These steps are optional. Broken links may result if they are not implemented correctly.</div>
                        <Text as="p">
                            To make collection links respect the shopper&apos;s vehicle selection, add the class <code>gv-collection-nav</code> to a parent element containing those links. Add it to the closest practical parent element.
                        </Text>
                        <DocumentationImage file="gv-collection.PNG" alt="Adding gv-collection-nav to a theme element" />
                        <Text as="p">
                            The class can be added to multiple elements, but it should only be applied to elements containing collection links. When implemented correctly, collection pages return only products matching the selected vehicle.
                        </Text>
                    </DocumentationSection>

                    <DocumentationSection id="product-page-updates" title="Product Page Updates">
                        <Text as="p">Follow these steps to show the shopper&apos;s selected vehicle on product pages.</Text>
                        <DocumentationImage file="product-fitment.png" alt="Vehicle fitment message on a product page" />
                        <Text as="p">
                            In the theme code editor, open the product template. It is usually in the Templates folder and may be named <ExternalLink href="https://shopify.com/admin/themes/current/?key=templates/product.liquid">product.liquid</ExternalLink>.
                        </Text>
                        <DocumentationImage file="product-liquid-file.jpg" alt="Opening product.liquid in the theme editor" />
                        <Text as="p">
                            The product template may reference a section. If so, make the following edits in that referenced file instead—for example, product-template.liquid in the Sections folder.
                        </Text>
                        <DocumentationImage file="section-ref.JPG" alt="Product template referencing a theme section" />
                        <Text as="p">Place this first snippet where the fitment note should appear, such as below the product title or SKU. Add wrapper markup if necessary to match the theme.</Text>
                        <CodeSample>{productFitmentSnippet}</CodeSample>
                        <Text as="p">Place this second snippet anywhere in the product template, but do not insert it inside a loop.</Text>
                        <CodeSample>{productTagsSnippet}</CodeSample>
                        <Text as="p">Save and test your changes.</Text>
                    </DocumentationSection>

                    <DocumentationSection id="my-garage" title="My Garage">
                        <Text as="p">
                            These advanced theme-editing steps allow signed-in shoppers to save vehicles to their account and view their saved vehicles. <a href="mailto:support@govedia.com">Contact us</a> if you need assistance.
                        </Text>
                        <DocumentationImage file="My-Garage.JPG" alt="Legacy My Garage storefront display" />
                        <Text as="p">Add this snippet to the appropriate Liquid template, then adjust its styling as needed.</Text>
                        <CodeSample>{garageSnippet}</CodeSample>
                        <Text as="p">Add the following sample CSS to the theme. It is already included when using the 2.0 integration method described above.</Text>
                        <CodeSample>{garageCss}</CodeSample>
                    </DocumentationSection>

                    <DocumentationSection id="managing-vehicles" title="Creating &amp; Managing Vehicles">
                        <Text as="p">
                            Vehicles combine Make, Model, and Year attributes. Manage them on the Vehicles page, where you can create, edit, or delete each attribute.
                        </Text>
                        <DocumentationImage file="Makes.PNG" alt="Legacy vehicle makes administration screen" />
                        <Text as="p">To create a vehicle:</Text>
                        <ol>
                            <li>
                                <strong>Add a make</strong> by entering a value in New Make and clicking Add Make. The table is automatically sorted alphabetically.
                                <DocumentationImage file="Add-Make.PNG" alt="Adding a make" />
                            </li>
                            <li>
                                Click the models link beside the make to create its models.
                                <DocumentationImage file="Add-Models.png" alt="Opening models for a make" />
                            </li>
                            <li><strong>Add a model</strong> by entering a value in New Model and clicking Add Model.</li>
                            <li>
                                Click the years link beside a model to manage its years.
                                <DocumentationImage file="Ford-Models.PNG" alt="Opening years for a model" />
                            </li>
                            <li><strong>Add years</strong> with the New Year field and Add Year button.</li>
                        </ol>
                        <Text as="p">You can edit or delete attributes directly from each Make, Model, and Year table. Click Save after editing a value.</Text>
                    </DocumentationSection>

                    <DocumentationSection id="vehicle-assignment" title="Assigning Vehicles to Products">
                        <Text as="p">
                            After creating vehicles, map them to products from Product Fitments. Select a Make, Model, and Year. The optional To Year Range field maps products to multiple years; for example, Ford F-150 from 2014 through 2017 assigns the products to all four model years.
                        </Text>
                        <Text as="p">
                            After selecting a vehicle, the product table shows its current mappings. Highlighted and checked rows are already mapped. Uncheck a product to remove the assignment or check another product to add it, then click Save Assignment.
                        </Text>
                        <DocumentationImage file="Mapping-Highlight.PNG" alt="Highlighted product vehicle mappings" />
                        <div className="LegacyDocumentation__tip">
                            <strong>Tip:</strong> The Make, Model, and Year selectors also accept typed input and display suggestions. If an attribute does not exist, the selector may offer an Add option.
                            <DocumentationImage file="Mapping-Add-Make.PNG" alt="Adding a make from a vehicle selector" />
                        </div>
                    </DocumentationSection>

                    <DocumentationSection id="video-tour" title="Video Tour">
                        <Text as="p">
                            A quick tour of the app&apos;s features. The original demo store was <ExternalLink href="https://govedia-development-site.myshopify.com">govedia-development-site.myshopify.com</ExternalLink>.
                        </Text>
                        <InlineStack align="start">
                            <div className="LegacyDocumentation__video">
                                <iframe
                                    src="https://www.youtube.com/embed/NxnA2oi9JKg?rel=0"
                                    title="PartFinder legacy app video tour"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        </InlineStack>
                    </DocumentationSection>
                </BlockStack>
            </Layout.Section>
        </Layout>
    </Page>
);
